import React, { useEffect, useState } from "react";
import { api_Routes } from "../../tools/api_Routes";
import { Helper } from "../../tools/Helper";
import {
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DataTable from "./data-table";

export const DepositTable = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [orderStatus, setOrderStatus] = useState();
  const [paymentStatus, setPaymentStatus] = useState();
  const [source, setSource] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [filtersChanged, setFiltersChanged] = useState(false);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const getData = async () => {
    setErrorMessage("");

    // Validate that if a start date is selected, an end date must also be selected
    if (dateRange.start && !dateRange.end) {
      setErrorMessage("End date is required if a start date is selected.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    // Build the query string dynamically
    const queryParams = [];
    if (dateRange.start && dateRange.end) {
      queryParams.push(
        `start_date=${dateRange.start.toISOString().split("T")[0]}`
      );
      queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
    }
    else if (dateRange.end)
      queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
    if (orderStatus && orderStatus !== "null")
      queryParams.push(`order_status=${orderStatus}`);
    if (paymentStatus && paymentStatus !== "null")
      queryParams.push(`payment_status=${paymentStatus}`);
    if (source && source !== "null") queryParams.push(`source=${source}`);
    const queryString = queryParams.length ? `${queryParams.join("&")}` : "";

    const url =`${
        api_Routes.Deposits.View
    }?${queryString}&per_page=${rowsPerPage}&page=${currentPage + 1}`

    const { response, message } = await Helper.Get({
      url,
      hasToken: true,
    });
    if (response) {
      setData(response);
    } else {
      setData([]);
    }
    setIsLoading(false);
  };

  // Effect for filter changes
  useEffect(() => {
    if (filtersChanged) {
      setCurrentPage(0);
      setFiltersChanged(false);
    } else {
      getData();
    }
  }, [filtersChanged, currentPage, rowsPerPage]);

  const handleResetFilters = () => {
    setCurrentPage(0);
    setDateRange({ start: null, end: null });
    if (orderStatus) setOrderStatus("null");
    if (paymentStatus) setPaymentStatus("null");
    setFiltersChanged(true);
  };

  return (
    <div>


      {/* Filter Section */}
      <div className="flex flex-col justify-between items-start mb-3 gap-4">
        {/* Date Range Filter */}
        <div className="w-full flex items-start flex-wrap lg:flex-nowrap justify-between gap-4">
          <div className="w-full">
            <LocalizationProvider dateAdapter={AdapterDayjs} className="w-full">
              <DatePicker
                label="Start Date"
                value={dateRange.start}
                className="w-full"
                onChange={(newValue) => {
                  setDateRange((prev) => ({ ...prev, start: newValue }));
                  setFiltersChanged(true);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </div>
          <div className="w-full">
            <LocalizationProvider dateAdapter={AdapterDayjs} className="w-full">
              <DatePicker
                label="End Date"
                value={dateRange.end}
                className="w-full"
                onChange={(newValue) => {
                  setDateRange((prev) => ({ ...prev, end: newValue }));
                  setFiltersChanged(true);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </div>



          {/* Order Status Filter */}
          <FormControl className="w-full">
            <InputLabel id="order-status-select-label">Status</InputLabel>
            <Select
              labelId="order-status-select-label"
              id="order-status-select"
              value={orderStatus || null}
              onChange={(e) => {
                setOrderStatus(e.target.value);
                setFiltersChanged(true);
              }}
              label="Order Status"
              sx={{ textAlign: "start" }}
            >
              <MenuItem value="null">All</MenuItem>
              <MenuItem value="success">success</MenuItem>
              <MenuItem value="pending">pending</MenuItem>
              <MenuItem value="fail">fail</MenuItem>
            </Select>
          </FormControl>
          {/*<FormControl className="w-full">*/}
          {/*  <InputLabel id="order-source-select-label">Order Source</InputLabel>*/}
          {/*  <Select*/}
          {/*    labelId="order-source-select-label"*/}
          {/*    id="order-source-select"*/}
          {/*    value={source || null}*/}
          {/*    onChange={(e) => {*/}
          {/*      setSource(e.target.value);*/}
          {/*      setFiltersChanged(true);*/}
          {/*    }}*/}
          {/*    label="Order Source"*/}
          {/*    sx={{ textAlign: "start" }}*/}
          {/*  >*/}
          {/*    <MenuItem value="internal">Internal</MenuItem>*/}
          {/*    <MenuItem value="external">External</MenuItem>*/}
          {/*  </Select>*/}
          {/*</FormControl>*/}
        </div>
        {errorMessage && <div>*{errorMessage}</div>}

        {/* Reset Button */}
        <Button
          variant="outlined"
          color="primary"
          onClick={handleResetFilters}
          disabled={
            !dateRange.start &&
            !dateRange.end &&
            (!orderStatus || orderStatus === "null") &&
            (!paymentStatus || paymentStatus === "null")
          }
        >
          Reset Filters
        </Button>
      </div>

      {/* Table and Pagination */}
      {isLoading ? (
        <LinearProgress sx={{ marginTop: 1 }} />
      ) : data && data?.data?.length > 0 ? (
        <>
          <div className="overflow-x-auto">
              <DataTable
                setData={setData}
                data={data?.data}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                filters={{ dateRange, orderStatus, paymentStatus, source }}
              />
          </div>
          <div className="mt-4 flex justify-center items-center">
            <TablePagination
              page={currentPage}
              className="mt-auto"
              onPageChange={handlePageChange}
              count={data.pagination.total}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              variant="outlined"
              shape="rounded"
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </div>
        </>
      ) : (
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  There are no data yet
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};
